import React from 'react';
import styled from 'styled-components';

import { useCardCarousel } from '../../hooks/useCardCarousel';
import CarouselPips from '../CarouselPips';

import { Eyebrow, StepsSubtitle, Title } from './headings';

type StepCardData = {
  key: string;
  image: string;
  label: string;
  title: string;
  description: string;
  mobileDescription?: string;
};

const BEHIND_THE_ADVICE_CARDS: StepCardData[] = [
  {
    key: 'coach_card',
    image: '/images/sections/behindTheAdvice/coach.png',
    label: 'The coach',
    title: 'Spots what you’d miss',
    description:
      'It reads weeks of your sleep, food, movement, weight and stress, and spots patterns you’d never see yourself.',
  },
  {
    key: 'content_card',
    image: '/images/sections/behindTheAdvice/content.png',
    label: 'The content',
    title: 'Made by Shido’s experts',
    description:
      'Courses, meal plans, podcasts and articles by Shido’s nutritionists, trainers and sleep experts.',
  },
  {
    key: 'data_card',
    image: '/images/sections/behindTheAdvice/data.png',
    label: 'Your data',
    title: 'Encrypted and erasable',
    description:
      'Encrypted, used only to coach you, and yours to delete whenever you like, no questions asked.',
  },
];

const BehindTheAdviceSection = () => {
  const steps = useCardCarousel();

  return (
    <section>
      <SectionIntro>
        <Eyebrow>Behind the advice</Eyebrow>
        <Title>An AI health coach, guided by experts.</Title>
        <StepsSubtitle>
          What’s reading your week, where the advice comes from, and where your
          data stays.
        </StepsSubtitle>
      </SectionIntro>
      <StepCardsRow ref={steps.ref} onPointerDown={steps.hold}>
        {BEHIND_THE_ADVICE_CARDS.map((step) => (
          <StepCard key={step.key}>
            <StepImage>
              <img src={step.image} alt={step.title} width={420} height={267} />
            </StepImage>
            <StepBody>
              <StepLabel>{step.label}</StepLabel>
              <StepTitle>{step.title}</StepTitle>
              <StepDescriptionRow>
                <StepAccent />
                <StepDescription>
                  <DesktopOnly>{step.description}</DesktopOnly>
                  <MobileOnly>
                    {step.mobileDescription ?? step.description}
                  </MobileOnly>
                </StepDescription>
              </StepDescriptionRow>
            </StepBody>
          </StepCard>
        ))}
      </StepCardsRow>
      <CarouselPips
        count={steps.count}
        activeIdx={steps.activePageIndex}
        onSelect={steps.goTo}
      />
    </section>
  );
};

const SectionIntro = styled.div`
  margin-top: 60px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-top: 10px;
  }
`;

const StepCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 390px));
  justify-content: center;
  gap: 40px;
  margin-top: 70px;
  padding: 0 50px;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    grid-template-columns: minmax(0, 560px);
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: flex;
    justify-content: flex-start;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding-left: 20px;
    gap: 12px;
    margin-top: 32px;
    padding: 0 20px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StepCard = styled.div`
  box-sizing: border-box;
  border-radius: 8px;
  border: 2px solid rgb(65 78 113 / 20%);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 5px 20px rgb(0 0 0 / 5%);
  overflow: hidden;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: calc(100vw - 76px);
    min-height: 360px;
    flex-shrink: 0;
    scroll-snap-align: start;
  }
`;

const StepImage = styled.div`
  height: 267px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: 191px;
  }
`;

const StepBody = styled.div`
  padding: 20px;
`;

const StepLabel = styled.p`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  margin: 0 0 9px;
`;

const StepTitle = styled.h5`
  font-size: 20px;
  font-weight: 700;
  line-height: 23px;
  color: ${({ theme }) => theme.colors.topicBlue};
  margin: 0 0 15px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: 14px;
  }
`;

const StepDescriptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const StepAccent = styled.span`
  flex-shrink: 0;
  align-self: stretch;
  width: 4px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme.colors.secondary};
`;

const StepDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 27px;
  color: ${({ theme }) => theme.colors.topicBlue};
  margin: 0;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: 14px;
    line-height: 23px;
  }
`;

const DesktopOnly = styled.span`
  font: inherit;
  color: inherit;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: none;
  }
`;

const MobileOnly = styled.span`
  display: none;
  font: inherit;
  color: inherit;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: inline;
  }
`;

export default BehindTheAdviceSection;
